import React, { useState } from "react";
import debounce from "debounce-promise";
import { makeStyles } from "@material-ui/core/styles";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import AsyncSelect from "react-select/async";

const customSelectStyles = {
  container: (base) => ({
    ...base,
    margin: "16px 0 8px",
  }),
  control: (base) => ({
    ...base,
    minHeight: 56,
    borderColor: "#DEE7EE",
  }),
};

const SelectCoveredEntity = (props) => {
  const [primaryEntityId, setPrimaryEntityId] = useState(null);
  const [enableSecondaryEntities, setEnableSecondaryEntities] = useState(false);
  const [secondaryEntities, setSecondaryEntities] = useState([{ key: 0, entityId: null }]);

  const loadOptions = (query) => fetchSearchTerm(query);
  const debouncedLoadOptions = debounce(loadOptions, 500, {
    leading: true,
  });

  const getSelectedEntityIds = () => {
    const ids = new Set();
    if (primaryEntityId) ids.add(primaryEntityId);
    secondaryEntities.forEach(({ entityId }) => {
      if (entityId) ids.add(entityId);
    });
    return ids;
  };

  const filterOutSelectedEntities = (unregisteredEntities) => {
    const selectedIds = getSelectedEntityIds();
    return unregisteredEntities.filter((entity) => !selectedIds.has(entity.value));
  };

  const handleSelectedPrimaryEntity = (selection) => {
    setPrimaryEntityId(selection ? selection.value : null);
    selection ? props.completeSelection(true) : props.completeSelection(false);
  };

  const handleSelectedSecondaryEntity = (key, selection) => {
    setSecondaryEntities((prev) =>
      prev.map((entry) =>
        entry.key === key ? { ...entry, entityId: selection ? selection.value : null } : entry
      )
    );
  };

  const fetchSearchTerm = async (query) => {
    let url = new URL("/api/entities", window.location.origin);
    url.search = new URLSearchParams({ query: query }).toString();

    const unregisteredEntities = await fetch(url)
      .then((response) => response.json())
      .then((data) => data)
      .catch((error) => {
        console.error("Error:", error);
      });

    return filterOutSelectedEntities(unregisteredEntities || []);
  };

  const renderSecondaryEntitySelects = () => {
    return secondaryEntities.map(({ key }) => (
      <div key={key} id={key}>
        <AsyncSelect
          components={{ DropdownIndicator: null, IndicatorSeparator: null }}
          openMenuOnFocus={false}
          openMenuOnClick={false}
          defaultOptions={false}
          name="secondary_entity_ids[]"
          placeholder={"Search Covered Entity *"}
          styles={customSelectStyles}
          isClearable={true}
          onChange={(selection) => handleSelectedSecondaryEntity(key, selection)}
          loadOptions={(query) => debouncedLoadOptions(query)}
        />
        <div
          style={{
            margin: "15px 0",
            textDecoration: "underline",
            color: "#718291",
            cursor: "pointer",
          }}
          onClick={() => {
            if (secondaryEntities.length === 1) return;

            setSecondaryEntities((prev) => prev.filter((entry) => entry.key !== key));
          }}
        >
          remove
        </div>
        <div className="register__divider" />
      </div>
    ));
  };

  const renderSecondaryEntityForm = () => {
    if (enableSecondaryEntities) {
      return (
        <>
          <p className="mt--16 t--md t--italic t--subtitle">
            Search for your covered entity name or 340B ID by typing in the search field(s) below.
          </p>
          <div className="register__divider" style={{ marginBottom: "unset" }} />
          <div className="flex flex-column align-items-stretch gap--16">
            <div>
              <div id={"secondaryEntityForm"}>{renderSecondaryEntitySelects()}</div>
              <div
                style={{
                  margin: "15px 0",
                  textDecoration: "underline",
                  color: "#3D71FF",
                  cursor: "pointer",
                  textAlign: "right",
                }}
                onClick={() => {
                  const nextKey = secondaryEntities[secondaryEntities.length - 1].key + 1;
                  setSecondaryEntities([...secondaryEntities, { key: nextKey, entityId: null }]);
                }}
              >
                + Add Covered Entity
              </div>
            </div>
          </div>
        </>
      );
    }
  };

  return (
    <div>
      <div className="register__step__title">2. Select your covered entity.</div>
      <div className="register__form" style={{ paddingTop: 30, paddingBottom: 40 }}>
        <div id={"primaryEntityInput"} />
        <p>Search for your covered entity name or 340B ID by typing in the search field below.</p>
        <AsyncSelect
          components={{ DropdownIndicator: null, IndicatorSeparator: null }}
          openMenuOnFocus={false}
          openMenuOnClick={false}
          name="primary_entity_id"
          placeholder={"Search Covered Entity *"}
          styles={customSelectStyles}
          onChange={handleSelectedPrimaryEntity}
          defaultOptions={false}
          isClearable={true}
          loadOptions={(query) => debouncedLoadOptions(query)}
        />
      </div>
      <div className="register__step__title">
        3. Select additional covered entities if applicable.
      </div>
      <div className="register__form" style={{ paddingTop: 30, paddingBottom: 40 }}>
        <div id={"secondaryEntityInputs"} />
        <p>Would you like to add another covered entity to your 340B ESP registration?</p>

        <FormControlLabel
          control={
            <Checkbox
              checked={enableSecondaryEntities}
              onChange={() => {
                setEnableSecondaryEntities(true);
              }}
              color="primary"
            />
          }
          label="Yes"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={!enableSecondaryEntities}
              onChange={() => {
                setEnableSecondaryEntities(false);
              }}
              color="primary"
            />
          }
          label="No"
        />

        {renderSecondaryEntityForm()}
      </div>
    </div>
  );
};

export default SelectCoveredEntity;
